import React from 'react';
import {Box, Typography} from '@mui/material';
import {styled, useTheme} from '@mui/material/styles';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIosNew';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

/**
 * Same skin as a TemplateCard's title bar — this row sits directly above the
 * grid of them, so anything else would read as a banner bolted onto the page.
 */
const EntryRow = styled(Box)(({theme}) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    width: '100%',
    padding: theme.spacing(1.5, 2),
    borderRadius: theme.shape.borderRadius,
    border: '1px solid rgba(255, 255, 255, 0.44)',
    background: 'linear-gradient(111.13deg, rgba(5, 22, 36, 0.92) 45.71%, rgba(2, 89, 137, 0.92) 117.8%)',
    cursor: 'pointer',
    transition: 'transform 0.2s ease',
    '&:hover': {
        transform: 'scale(1.01)',
    },
}));

/** The circular chevron a TemplateCard uses, so the affordance is the one the
 *  user has already learned on this screen. */
const ArrowButton = styled(Box)(({theme}) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    flexShrink: 0,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    color: 'white',
}));

/**
 * The way into "My creations" from the templates home.
 *
 * It lives here because the header cannot carry it on a phone: that toolbar is
 * already logo + greeting + logout, and a fourth control crowded it to the
 * point of illegibility. This is also the more discoverable place — the home
 * screen is where a user starts.
 */
const CreationsHomeEntry: React.FC = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const theme = useTheme();
    const isRtl = theme.direction === 'rtl';

    const open = () => navigate('/creations');

    return (
        <EntryRow
            role="button"
            tabIndex={0}
            onClick={open}
            onKeyDown={(event: React.KeyboardEvent) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    open();
                }
            }}
        >
            <PhotoLibraryOutlinedIcon sx={{color: 'white'}}/>
            <Box sx={{flex: 1, minWidth: 0}}>
                <Typography variant="body1" sx={{color: 'white', fontWeight: 500}}>
                    {t('main.creations.nav')}
                </Typography>
                <Typography
                    variant="caption"
                    sx={{
                        color: 'rgba(255,255,255,0.7)',
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {t('main.creations.homeEntryCaption')}
                </Typography>
            </Box>
            {/* The chevron points the way the language reads. */}
            <ArrowButton>
                {isRtl ? <ArrowBackIosIcon fontSize="small"/> : <ArrowForwardIosIcon fontSize="small"/>}
            </ArrowButton>
        </EntryRow>
    );
};

export default CreationsHomeEntry;
